/* ==========================================================================
   Supply6 — cart freebie engine  (assets/s6-freebies.js)
   ==========================================================================

   WHAT THIS FILE DOES
   -------------------
   It keeps the free (₹0) gift lines in the cart correct. It watches the cart,
   works out which gifts the shopper has earned, and adds or removes those
   lines. It replaces the freebie behaviour the old GoKwik cart drawer gave us.

   THE THREE RULES (the current setup)
   -----------------------------------
   1. Gut Essentials Duo in the cart      -> free 500ml Bottle
   2. Protein Essentials Duo in the cart  -> free Steel Pro Shaker
   3. Supply6 Salts, 36 or 54 sachets     -> free 500ml Bottle
      (rule 3 only runs inside a start/end date window)

   One gift per order, never two of the same - including old carts that still
   carry a GoKwik-era freebie line (properties _gokwik_freebie / _gkfc): those
   count as the same gift and the extra line is removed.

   THE GIFT ARRIVES WITH THE PRODUCT, NOT AFTER IT
   -----------------------------------------------
   The gift line is added in the SAME /cart/add.js request as the product the
   shopper tapped, so the cart updates once and the drawer draws once. There is
   no "product appears, then a gift appears" flicker. This works because we keep
   a copy of the last cart in memory, so the decision can be made on the spot,
   while the request is still being built. If two rules both grant the
   bottle, the shopper still gets exactly one bottle. And because rule 2
   already gives a Pro Shaker, the separate cart-value milestone in custom.js
   is held back so nobody ever gets two shakers.

   WHERE THE SETTINGS ARE
   ----------------------
   Shopify admin -> Online Store -> Themes -> Customize -> Theme settings ->
   "Supply6 Freebies". That group has the master on/off switch, an on/off per
   rule, and the Salts promo start/end dates. Anything set there beats the
   defaults written in the CONFIG block below, so day-to-day changes need no
   code edit at all. Only things the theme editor cannot express — variant ids
   and trigger product ids — have to be changed in this file.

   Dates are full ISO strings WITH the +05:30 India offset, e.g.
       "2026-08-29T00:00:00+05:30"
   Use null (or a blank field in the theme editor) for "no limit".

   REMOVING A GIFT, AND ADDING IT BACK
   -----------------------------------
   Every gift row in the drawer has a small bin button. Tapping it:
     1. remembers "this gift was dismissed" in sessionStorage (browser tab
        memory only) BEFORE the removal request is sent, so this engine does
        not immediately put the gift back, and
     2. removes the line.
   The gift then reappears as an "Add back" row at the BOTTOM of the item
   list — but only while the cart still qualifies for it. Take the trigger
   product out of the cart and the row disappears too. "Add back" clears the
   flag and re-adds the gift. The memory is per browser tab and is also
   cleared automatically once the cart stops qualifying, so re-adding the
   trigger product grants the gift again as normal.

   WHERE EACH PIECE OF CART LOGIC LIVES
   ------------------------------------
   sections/mini-cart.liquid   the drawer's HTML and CSS. No JavaScript in it.
                               Decides which lines look like gifts, prints the
                               "Free · Qty 1" pill and the bin button.
   assets/custom.js            everything else about the cart (jQuery): open /
                               close, quantity changes, coupons, and the
                               cart-VALUE milestone gifts (addFreeProduct).
   assets/s6-freebies.js       this file. Product-triggered gifts, the
                               dismissed / add-back behaviour, and holding the
                               milestone shaker back when rule 2 applies.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- CONFIG */

  var S6_FREEBIE_CONFIG = {

    // The ₹0 products we are allowed to give away.
    // Each key is a stable id used by the rules below and written onto the
    // cart line as the `_s6_freebie` property.
    freebies: {
      'salts-bottle': {
        variantId: 51200397541599,          // 🎁 500ml Bottle — SKU Salts-Bottle — ₹0 — ACTIVE
        label: 'Free 500ml Bottle'
      },
      'pro-shaker': {
        variantId: 51238728237279,          // 🎁 Steel Pro Shaker - Snow — SKU S6-PTN-SH-W — ₹0 — ACTIVE
        label: 'Free Steel Pro Shaker'
      }
    },

    rules: [
      {
        id: 'gutduo-salts-bottle',
        freebie: 'salts-bottle',
        // Gut Essentials Duo — both variants (GUTDUO-360-UN, GUTDUO-360-OB)
        triggerProductIds: [9423105851615],
        // The same product listed by VARIANT. An add-to-cart request only says
        // which variant is being added, never which product, so this is what
        // lets the gift ride along in that same request.
        triggerProductVariantIds: [50218315612383, 50218315645151],
        start: null,
        end: null
      },
      {
        id: 'proteinduo-shaker',
        freebie: 'pro-shaker',
        // Protein Essentials Duo — SKU PTNDUO-WCH-PCH
        triggerProductIds: [9461711306975],
        triggerProductVariantIds: [50814607524063],
        start: null,
        end: null
      },
      {
        id: 'salts-36-54-bottle',
        freebie: 'salts-bottle',
        // Supply6 Salts, "Sachets" option = 36 or 54, across all five flavours
        triggerVariantIds: [
          48553348989151, 48553349021919,   // Lime           36 / 54
          46594404647135, 46594404679903,   // Blue Raspberry 36 / 54
          46653429842143, 46653429874911,   // Mixed Flavours 36 / 54
          46925674512607, 46925674545375,   // Watermelon     36 / 54
          46925684605151, 46925684637919    // Mango          36 / 54
        ],
        start: '2026-08-29T00:00:00+05:30',
        end:   '2026-08-31T23:59:59+05:30'
      }
    ],

    // When the Protein Essentials Duo is in the cart it already grants the
    // Pro Shaker, so the cart-value milestone must not grant a second one.
    // Only ever one Pro Shaker free per order.
    milestoneShaker: {
      // Which freebie key represents the shaker.
      freebie: 'pro-shaker',
      // Rules that, when active, suppress the milestone shaker.
      suppressedByRuleIds: ['proteinduo-shaker'],
      // Text used to find the shaker segment in the milestone progress bar,
      // so it can be shown as already unlocked. Matched case-insensitively.
      progressLabelMatch: 'shaker'
    },

    // Property name written on every freebie line.
    propertyName: '_s6_freebie',

    debug: false
  };

  /* ------------------------------------------------ THEME-SETTINGS OVERRIDE */
  /*
     snippets/js-variables.liquid emits window.themeVariables.settings.s6Freebies
     from the "Supply6 Freebies" group in Theme settings. Whatever is set there
     wins over the defaults above.

     A key that is null/undefined means the merchant has never saved that
     setting, so the hardcoded default above is kept. Only an explicit value
     overrides — that way a missing settings_data entry can never silently
     switch the whole engine off.
  */

  // Master switch. false = this file does nothing at all.
  var S6_ENGINE_ENABLED = true;

  // Which theme-setting key drives which rule id.
  var S6_RULE_SETTING_KEYS = {
    'gutduo-salts-bottle': 'gutduoBottle',
    'proteinduo-shaker': 'proteinduoShaker',
    'salts-36-54-bottle': 'saltsPromoEnabled'
  };

  function findRule(id) {
    for (var i = 0; i < S6_FREEBIE_CONFIG.rules.length; i++) {
      if (S6_FREEBIE_CONFIG.rules[i].id === id) return S6_FREEBIE_CONFIG.rules[i];
    }
    return null;
  }

  function applyThemeSettings() {
    var s = window.themeVariables &&
            window.themeVariables.settings &&
            window.themeVariables.settings.s6Freebies;

    if (!s) return;                              // no settings emitted: keep defaults

    if (typeof s.debug === 'boolean') S6_FREEBIE_CONFIG.debug = s.debug;
    if (typeof s.enabled === 'boolean') S6_ENGINE_ENABLED = s.enabled;

    // Per-rule on/off switches.
    Object.keys(S6_RULE_SETTING_KEYS).forEach(function (ruleId) {
      var rule = findRule(ruleId);
      if (!rule) return;
      var value = s[S6_RULE_SETTING_KEYS[ruleId]];
      if (typeof value === 'boolean') rule.enabled = value;
    });

    // Salts promo window. A blank string means "no limit on that side";
    // null/undefined means the setting was never saved, so keep the default.
    var salts = findRule('salts-36-54-bottle');
    if (salts) {
      if (typeof s.saltsPromoStart === 'string') {
        salts.start = s.saltsPromoStart.trim() === '' ? null : s.saltsPromoStart.trim();
      }
      if (typeof s.saltsPromoEnd === 'string') {
        salts.end = s.saltsPromoEnd.trim() === '' ? null : s.saltsPromoEnd.trim();
      }
      // Server-side belt: Liquid compared the same window against the shop
      // clock when the page was rendered. The promo only counts as live when
      // the device clock AND the server agree, so a shopper with a wrong or
      // deliberately-shifted clock cannot open the promo early or keep it
      // running late. Because the flag is baked into cached HTML it can lag
      // the true start/end by however long the page sits in cache (usually a
      // few minutes) — acceptable for a promo measured in days.
      if (typeof s.saltsPromoServerActive === 'boolean') {
        salts.serverActive = s.saltsPromoServerActive;
      }
    }
  }

  applyThemeSettings();

  /* ------------------------------------------------------------- UTILITIES */

  function log() {
    if (!S6_FREEBIE_CONFIG.debug || !window.console) return;
    console.log.apply(console, ['[s6-freebies]'].concat([].slice.call(arguments)));
  }

  function toId(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  // A rule is in-window when now is between start and end (inclusive).
  // Parsing an ISO string that carries its own +05:30 offset gives the
  // correct absolute instant regardless of the shopper's own timezone.
  function ruleInWindow(rule, now) {
    if (rule.start) {
      var s = Date.parse(rule.start);
      if (!isNaN(s) && now < s) return false;
    }
    if (rule.end) {
      var e = Date.parse(rule.end);
      if (!isNaN(e) && now > e) return false;
    }
    return true;
  }

  /* --------------------------------------------------- DISMISSED GIFTS ---- */
  /*
     When a shopper removes a free gift on purpose we must not put it straight
     back. The gift's key is stored in sessionStorage (this browser tab only).
     While a key is stored:
       - the reconciler below never re-adds that gift,
       - an "Add back" row is printed at the bottom of the item list instead,
         but only while the cart still qualifies for the gift.
     Storage can throw (private mode, blocked cookies) so every access is
     wrapped: the worst case is that the dismissal is simply not remembered.
  */
  var DISMISS_KEY = 's6_freebies_dismissed';
  var dismissedCache = {};

  function readDismissed() {
    try {
      var raw = window.sessionStorage.getItem(DISMISS_KEY);
      var obj = raw ? JSON.parse(raw) : null;
      dismissedCache = (obj && typeof obj === 'object') ? obj : {};
    } catch (e) {
      // storage unavailable — keep whatever is in memory
    }
    return dismissedCache;
  }

  function writeDismissed(map) {
    dismissedCache = map || {};
    try { window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissedCache)); }
    catch (e) { /* not persisted; in-memory copy still works for this page */ }
  }

  function isDismissed(key) { return !!(key && dismissedCache[key]); }

  function setDismissed(key, on) {
    if (!key) return;
    var map = readDismissed();
    if (on) { map[key] = 1; } else { delete map[key]; }
    writeDismissed(map);
  }

  readDismissed();

  /* ------------------------------------------- MILESTONE GIFTS (custom.js) */
  /*
     assets/custom.js gives a gift when the cart value passes a milestone
     (Theme settings > Milestone Base Free Gift). Those lines carry
     `_type: "free_gift"`. We give them the keys "milestone-1" / "milestone-2"
     so they can be dismissed and added back exactly like our own freebies.
  */
  function milestoneGift(n) {
    var s = window.themeVariables && window.themeVariables.settings;
    if (!s) return null;
    var p = s['freeProduct_' + n];
    var vid = p && p.variants && p.variants[0] && toId(p.variants[0].id);
    if (!vid) return null;
    return {
      key: 'milestone-' + n,
      variantId: vid,
      label: p.title || 'Free gift',
      // The theme setting is in rupees; the cart is in paise.
      thresholdPaise: (parseInt(s['freeProduct_milestone_' + n] || '0', 10) || 0) * 100
    };
  }

  function milestoneGifts() {
    var out = [];
    [1, 2].forEach(function (n) { var g = milestoneGift(n); if (g) out.push(g); });
    return out;
  }

  function milestoneGiftByKey(key) {
    var found = null;
    milestoneGifts().forEach(function (g) { if (g.key === key) found = g; });
    return found;
  }

  // Does the cart currently earn this milestone gift?
  function milestoneEarned(cart, key, suppressed) {
    var g = milestoneGiftByKey(key);
    if (!g || !g.thresholdPaise) return false;
    // The Protein Duo rule already grants the shaker, so the milestone one is
    // not "earned" on top of it.
    if (suppressed && g.variantId === shakerVariant()) return false;
    var total = cart.original_total_price || cart.total_price || 0;
    return total >= g.thresholdPaise;
  }

  function isFreebieLine(item) {
    return !!(item && item.properties && item.properties[S6_FREEBIE_CONFIG.propertyName]);
  }

  // Carts created before the GoKwik drawer was switched off still hold ₹0
  // freebie lines tagged with GoKwik's own properties. They are the same
  // products, so we treat them as ours.
  function isLegacyGokwikFreebieLine(item) {
    var p = (item && item.properties) || {};
    return !!(p._gokwik_freebie || p._gkfc);
  }

  // Which freebie is this variant, if any? Lets us recognise a freebie line by
  // the product it is rather than only by the property we wrote on it.
  function freebieKeyForVariant(variantId) {
    var vid = toId(variantId);
    if (vid === null) return null;
    var match = null;
    Object.keys(S6_FREEBIE_CONFIG.freebies).forEach(function (key) {
      if (toId(S6_FREEBIE_CONFIG.freebies[key].variantId) === vid) match = key;
    });
    return match;
  }

  // The freebie key for a cart line, whoever put it there: our own property
  // first, then a legacy GoKwik line or any ₹0 line whose variant is one of
  // ours. Lines added by the milestone engine in custom.js are deliberately
  // NOT matched here - they belong to that engine.
  function freebieKeyForLine(item) {
    if (!item) return null;
    var props = item.properties || {};
    var key = props[S6_FREEBIE_CONFIG.propertyName];
    if (key) return key;
    if (isMilestoneGiftLine(item)) return null;
    if (!isLegacyGokwikFreebieLine(item) && item.price !== 0) return null;
    return freebieKeyForVariant(item.variant_id);
  }

  // Does this rule fire for a given product / variant? Either id may be null:
  // a cart line knows both, an add-to-cart request only knows the variant.
  function ruleHits(rule, productId, variantId) {
    if (productId !== null && rule.triggerProductIds &&
        rule.triggerProductIds.indexOf(productId) !== -1) return true;
    if (variantId === null) return false;
    if (rule.triggerVariantIds && rule.triggerVariantIds.indexOf(variantId) !== -1) return true;
    if (rule.triggerProductVariantIds &&
        rule.triggerProductVariantIds.indexOf(variantId) !== -1) return true;
    return false;
  }

  // Is this rule live right now (switched on, inside its date window)?
  function ruleLive(rule, now) {
    if (rule.enabled === false) return false;
    if (rule.serverActive === false) return false;
    return ruleInWindow(rule, now);
  }

  // Which rules are currently satisfied by the cart?
  function activeRuleIds(cart) {
    var now = Date.now();
    var ids = {};

    (cart.items || []).forEach(function (item) {
      if (isFreebieLine(item) || isLegacyGokwikFreebieLine(item)) return;  // freebies never trigger freebies

      var pid = toId(item.product_id);
      var vid = toId(item.variant_id);

      S6_FREEBIE_CONFIG.rules.forEach(function (rule) {
        if (ids[rule.id]) return;
        if (!ruleLive(rule, now)) return;
        if (ruleHits(rule, pid, vid)) ids[rule.id] = true;
      });
    });

    return ids;
  }

  // The set of freebie keys the cart *should* contain, one line each, qty 1.
  // Two different rules granting the same freebie collapse to one line —
  // this is what makes "Gut Duo + Salts 36 = still only one bottle" work.
  function desiredFreebies(activeIds) {
    var wanted = {};
    S6_FREEBIE_CONFIG.rules.forEach(function (rule) {
      if (activeIds[rule.id] && S6_FREEBIE_CONFIG.freebies[rule.freebie]) {
        wanted[rule.freebie] = true;
      }
    });
    return wanted;
  }

  function milestoneShakerSuppressed(activeIds) {
    var cfg = S6_FREEBIE_CONFIG.milestoneShaker;
    if (!cfg) return false;
    return cfg.suppressedByRuleIds.some(function (id) { return !!activeIds[id]; });
  }

  /* ------------------------------------- SUPPRESS THE MILESTONE PRO SHAKER */
  /*
     The milestone engine in custom.js (addFreeProduct) identifies its gift
     purely by variant id, so it would both (a) add a second shaker and
     (b) delete ours whenever the cart total sits under ₹3299 — an add/remove
     loop. We neutralise it for the duration of each call by hiding
     freeProduct_2 from it, which stops its add AND its remove.
  */
  var milestoneSuppressionOn = false;

  function patchMilestoneEngine() {
    var original = window.addFreeProduct;
    if (typeof original !== 'function' || original.__s6Patched) return false;

    var patched = function (event, cart) {
      var settings = window.themeVariables && window.themeVariables.settings;
      var shakerVariantId = shakerVariant();
      var stash = {};

      // Decide from the cart we have been handed, right here, right now.
      // This used to read a flag that our own (debounced) pass set a moment
      // LATER, so on the first refresh after the Protein Duo went in the flag
      // was still false, custom.js added a second shaker, and we then had to
      // take it away again. That add-then-remove was the flicker the shopper
      // saw. Deciding from the cart in hand means it never happens.
      var liveCart = (cart && cart.items) ? cart : cachedCart;
      var suppressShaker = false;

      if (liveCart && liveCart.items) {
        suppressShaker = milestoneShakerSuppressed(activeRuleIds(liveCart));

        // Also hold back when a shaker is ALREADY in the cart and it is not
        // custom.js's own gift line - ours, or one left over from GoKwik.
        // (We must not hide its own line from it, or it could never take the
        // gift back when the cart total drops below the milestone again.)
        liveCart.items.forEach(function (item) {
          if (toId(item.variant_id) !== shakerVariantId) return;
          if (isMilestoneGiftLine(item)) return;
          suppressShaker = true;
        });
      }
      milestoneSuppressionOn = suppressShaker;

      if (settings) {
        [1, 2].forEach(function (n) {
          var field = 'freeProduct_' + n;
          var product = settings[field];
          if (!product) return;

          var vid = product.variants && product.variants[0] && toId(product.variants[0].id);

          // (a) this shaker is already covered — never two
          var hideForDuo = suppressShaker && vid === shakerVariantId;
          // (b) the shopper removed this gift on purpose in this browser tab
          var hideForDismissal = isDismissed('milestone-' + n);

          if (hideForDuo || hideForDismissal) {
            stash[field] = product;
            settings[field] = null;
            log('milestone gift', n, 'hidden for this pass', hideForDuo ? '(covered)' : '(dismissed)');
          }
        });
      }

      try {
        return original.apply(this, arguments);
      } finally {
        Object.keys(stash).forEach(function (field) { settings[field] = stash[field]; });
      }
    };

    patched.__s6Patched = true;
    window.addFreeProduct = patched;
    log('milestone engine patched');
    return true;
  }

  function shakerVariant() {
    var cfg = S6_FREEBIE_CONFIG.milestoneShaker;
    if (!cfg) return null;
    var f = S6_FREEBIE_CONFIG.freebies[cfg.freebie];
    return f ? toId(f.variantId) : null;
  }

  /* ----------------------------------------------- DRAWER PRESENTATION ---- */
  /*
     The drawer's Liquid does not know about `_s6_freebie`, and it renders the
     quantity +/- controls for gift lines too. Rather than rewrite the 114 KB
     mini-cart.liquid, we decorate the rendered DOM.

     The drawer prints cart.items in order into <line-item> elements, so the
     Nth <line-item> is cart.items[N]. If the counts ever disagree we skip,
     rather than mislabel a line.
  */

  var STYLE_ID = 's6-freebies-style';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    // The "Free · Qty 1" pill, the bin button and the "Add back" row are all
    // styled in sections/mini-cart.liquid — one place, easy to find. These two
    // rules are only a safety net for the moment before the drawer re-renders.
    var css = [
      '.s6-freebie-line .quantity-selector,',
      '.s6-freebie-line .line-item__remove-button { display: none !important; }',
      '.line-item--gift .quantity-selector { display: none !important; }'
    ].join('\n');

    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(el);
  }

  function isMilestoneGiftLine(item) {
    var p = (item && item.properties) || {};
    return p._type === 'free_gift' || !!p._milestone_gift_level || !!p._free_gift_of;
  }

  function decorateLines(cart) {
    var lists = document.querySelectorAll('.mini-cart-line-items');
    if (!lists.length || !cart || !cart.items) return;

    // Match each rendered row to its cart line by the key PRINTED ON THE ROW,
    // never by its position. The drawer lists the products first and the free
    // gifts grouped underneath, so the Nth row on screen is not the Nth line in
    // the cart. sections/mini-cart.liquid prints data-line-key on every row.
    var byKey = {};
    var byVariant = {};
    cart.items.forEach(function (item) {
      byKey[item.key] = item;
      if (!byVariant[item.variant_id]) byVariant[item.variant_id] = item;
    });

    Array.prototype.forEach.call(lists, function (list) {
      var nodes = list.querySelectorAll('line-item');

      Array.prototype.forEach.call(nodes, function (el) {
        var item = byKey[el.getAttribute('data-line-key')];
        if (!item) item = byVariant[el.getAttribute('data-variant-id')];
        if (!item) return;          // a row we cannot place - leave it alone
        var key = freebieKeyForLine(item);

        // The line key is what /cart/change.js needs to remove this exact row.
        el.setAttribute('data-s6-line-key', item.key);

        if (key) {
          el.classList.add('line-item--gift', 's6-freebie-line');
          el.setAttribute('data-s6-freebie', key);
        } else if (isMilestoneGiftLine(item)) {
          el.classList.add('line-item--gift', 's6-freebie-line');
          el.setAttribute('data-s6-milestone-gift', item.variant_id);
        } else {
          el.classList.remove('s6-freebie-line');
          el.removeAttribute('data-s6-freebie');
          el.removeAttribute('data-s6-milestone-gift');
        }
      });
    });
  }

  /* --------------------------------------------------- PROGRESS BAR TWEAKS */
  /*
     When the Protein Duo already grants the shaker, show that milestone as
     unlocked rather than dangling an unreachable reward in front of the
     shopper. Purely cosmetic and re-applied after every re-render.
  */
  function markShakerMilestoneUnlocked(suppressed) {
    var cfg = S6_FREEBIE_CONFIG.milestoneShaker;
    if (!cfg || !cfg.progressLabelMatch) return;

    var needle = cfg.progressLabelMatch.toLowerCase();

    // Price-based bar (.reward-bar__segment) and quantity-based bar (.check-point)
    var nodes = document.querySelectorAll('.reward-bar__segment, .shipiing-progress-track .check-point');

    Array.prototype.forEach.call(nodes, function (node) {
      var text = (node.textContent || '').toLowerCase();
      if (text.indexOf(needle) === -1) return;

      if (suppressed) {
        node.classList.add('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      } else if (node.classList.contains('s6-freebie-unlocked')) {
        node.classList.remove('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      }
    });
  }

  /* ------------------------------------------------------- RECONCILIATION  */

  var chain = Promise.resolve();   // serialises every cart write we make
  var busy  = false;              // true while our own writes are in flight
  var lastCart = null;            // most recent cart we reconciled against
  var cachedCart = null;          // synchronous copy, so the co-add decision
                                  // can be made while a request is being built
  var lastEarned = {};            // freebie keys the cart currently qualifies for
  var lastSuppressed = false;     // true while the Protein Duo covers the shaker

  // True only while WE are talking to the cart, so our own requests are not
  // mistaken for the shopper's and given a gift of their own.
  var s6OwnWrite = false;

  function s6Post(url, payload, tag) {
    s6OwnWrite = true;
    var request;
    try {
      request = fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    } finally {
      s6OwnWrite = false;      // the request is already on its way
    }
    return request.then(function (r) {
      if (!r.ok) log(tag + ' failed', r.status);
      return r;
    }).catch(function (e) { log(tag + ' error', e); });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cachedCart = cart; return cart; });
  }

  // One request, however many gifts.
  function addItems(items) {
    if (!items.length) return Promise.resolve();
    return s6Post('/cart/add.js', { items: items }, 'add');
  }

  function freebieItem(freebieKey) {
    var f = S6_FREEBIE_CONFIG.freebies[freebieKey];
    var props = {};
    props[S6_FREEBIE_CONFIG.propertyName] = freebieKey;
    return { id: f.variantId, quantity: 1, properties: props };
  }

  // One request, however many lines. Keyed by LINE key rather than variant id,
  // so two lines holding the same variant can be treated separately (that is
  // what the legacy-GoKwik de-duplication needs). Confirmed against the live
  // store: /cart/update.js accepts line keys.
  function updateLines(updates) {
    if (!Object.keys(updates).length) return Promise.resolve();
    return s6Post('/cart/update.js', { updates: updates }, 'update');
  }

  function setLineQuantity(key, quantity) {
    var updates = {};
    updates[key] = quantity;
    return updateLines(updates);
  }

  // Work out the difference between what the cart has and what it should
  // have. Returns null when nothing needs doing — the loop guard.
  function planFor(cart) {
    var activeIds = activeRuleIds(cart);
    var earned    = desiredFreebies(activeIds);
    var suppressShaker = milestoneShakerSuppressed(activeIds);

    // A gift the shopper deliberately removed stays out of the cart until they
    // press "Add back". Everything below works off `wanted`, so a dismissed
    // gift is simply never added — no fighting the shopper.
    var wanted = {};
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) wanted[key] = true;
    });

    var toAdd    = [];
    var toRemove = [];
    var toFix    = [];
    var seen     = {};

    // Group every line that IS one of our freebies, no matter who added it -
    // us, or the old GoKwik drawer on a cart made before the switch. This is
    // what stops a shopper ending up with two Pro Shakers.
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var key = freebieKeyForLine(item);
      if (!key || !S6_FREEBIE_CONFIG.freebies[key]) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    Object.keys(groups).forEach(function (key) {
      var lines = groups[key];

      // Keep one line only. Prefer the line we added ourselves (it carries the
      // property the drawer and the add-back row rely on), else the first.
      var keeper = null;
      lines.forEach(function (it) { if (!keeper && isFreebieLine(it)) keeper = it; });
      if (!keeper) keeper = lines[0];

      var keepThisFreebie = !!wanted[key];

      lines.forEach(function (it) {
        if (!keepThisFreebie || it !== keeper) {
          toRemove.push(it.key);            // not earned, dismissed, or a duplicate
          return;
        }
        seen[key] = true;
        if (it.quantity !== 1) toFix.push(it.key);   // force back to qty 1
      });
    });

    // `seen` is set by the group loop above, which counts legacy lines too, so
    // we never add a second line of a freebie the cart already holds.
    Object.keys(wanted).forEach(function (key) {
      if (!seen[key]) toAdd.push(key);
    });

    // If the Protein Duo is granting the shaker, clear out any shaker the
    // cart-value milestone had already dropped in (it carries _type:free_gift,
    // not our property, so the loop above would not have caught it).
    if (suppressShaker) {
      var shakerId = shakerVariant();
      (cart.items || []).forEach(function (item) {
        if (isFreebieLine(item)) return;
        if (toId(item.variant_id) !== shakerId) return;
        var props = item.properties || {};
        if (props._type === 'free_gift' || props._milestone_gift_level) {
          toRemove.push(item.key);
          log('removing milestone shaker — Protein Duo already grants one');
        }
      });
    }

    if (!toAdd.length && !toRemove.length && !toFix.length) return null;
    return { toAdd: toAdd, toRemove: toRemove, toFix: toFix };
  }

  // At most TWO requests for any amount of tidying up: one update, one add.
  // Nothing re-renders in between - the drawer is refreshed once, afterwards.
  function applyPlan(plan) {
    var updates = {};
    plan.toRemove.forEach(function (key) { updates[key] = 0; });
    plan.toFix.forEach(function (key) { updates[key] = 1; });

    return updateLines(updates).then(function () {
      return addItems(plan.toAdd.map(freebieItem));
    });
  }

  function reconcile(cartFromEvent) {
    chain = chain.then(function () {
      if (busy) return;
      busy = true;

      return Promise.resolve(cartFromEvent || fetchCart())
        .then(function (cart) {
          if (!cart || !cart.items) return null;

          // Keep the milestone-suppression flag and the progress bar in step
          // with the cart on every pass, whether or not we change anything.
          var activeIds  = activeRuleIds(cart);
          var suppressed = milestoneShakerSuppressed(activeIds);
          milestoneSuppressionOn = suppressed;
          lastCart = cart;
          cachedCart = cart;
          lastEarned = desiredFreebies(activeIds);
          lastSuppressed = suppressed;
          forgetStaleDismissals(cart, lastEarned, suppressed);
          markShakerMilestoneUnlocked(suppressed);
          decorateLines(cart);
          renderGiftBackSlot(cart, lastEarned, suppressed);

          var plan = planFor(cart);
          if (!plan) { log('cart already correct'); return null; }

          log('applying', plan);
          return applyPlan(plan).then(function () { return true; });
        })
        .then(function (changed) {
          if (!changed) return;
          // Re-render the drawer through the theme's own helper so every
          // other listener sees a consistent cart.
          if (typeof window.themeRefreshCart === 'function') {
            return window.themeRefreshCart(false);
          }
          return fetchCart().then(function (cart) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true, detail: { cart: cart }
            }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true, detail: { cart: cart, openMiniCart: false }
            }));
          });
        })
        .catch(function (e) { log('reconcile error', e); })
        .then(function () { busy = false; });
    });

    return chain;
  }

  /* ------------------------------------------- "YOUR FREE GIFT" ADD-BACK ROW */
  /*
     Printed at the BOTTOM of the item list, one row per gift the shopper has
     earned but removed. Never offers a gift the cart has not earned.
     Styling lives in sections/mini-cart.liquid (.s6-giftback).
  */
  var SLOT_CLASS = 's6-giftback';

  // Forget a dismissal once the cart stops qualifying, so adding the trigger
  // product again simply grants the gift again.
  function forgetStaleDismissals(cart, earned, suppressed) {
    var map = readDismissed();
    var changed = false;

    Object.keys(map).forEach(function (key) {
      var stillEarned = key.indexOf('milestone-') === 0
        ? milestoneEarned(cart, key, suppressed)
        : !!earned[key];
      if (!stillEarned) { delete map[key]; changed = true; }
    });

    if (changed) writeDismissed(map);
  }

  function giftBackOffers(cart, earned, suppressed) {
    var offers = [];

    // our own product-triggered freebies
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) return;
      var f = S6_FREEBIE_CONFIG.freebies[key];
      if (f) offers.push({ key: key, label: f.label });
    });

    // cart-value milestone gifts from custom.js
    milestoneGifts().forEach(function (g) {
      if (!isDismissed(g.key)) return;
      if (!milestoneEarned(cart, g.key, suppressed)) return;
      offers.push({ key: g.key, label: g.label });
    });

    return offers;
  }

  function renderGiftBackSlot(cart, earned, suppressed) {
    var lists = document.querySelectorAll('.mini-cart-line-items');
    if (!lists.length || !cart) return;

    var offers = giftBackOffers(cart, earned || {}, !!suppressed);
    var signature = offers.map(function (o) { return o.key; }).join('|');

    Array.prototype.forEach.call(lists, function (list) {
      var old = list.querySelectorAll('.' + SLOT_CLASS);

      // Already showing exactly these offers? Then leave the DOM alone. This
      // matters: re-printing would itself be a change, which would wake the
      // re-render watcher below, which would re-print... round and round.
      // The marker is a plain JS property, so it disappears by itself when the
      // drawer swaps in fresh HTML.
      if (list.__s6Slot === signature && old.length === offers.length) return;
      list.__s6Slot = signature;

      // clear whatever we printed last time, then print the current offers
      Array.prototype.forEach.call(old, function (n) {
        if (n.parentNode) n.parentNode.removeChild(n);
      });

      offers.forEach(function (offer) {
        var row = document.createElement('div');
        row.className = SLOT_CLASS;

        var text = document.createElement('div');
        text.className = 's6-giftback__text';
        var strong = document.createElement('b');
        strong.appendChild(document.createTextNode('Your free gift'));
        text.appendChild(strong);
        text.appendChild(document.createTextNode(offer.label + ' — yours free with this order'));

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 's6-giftback__add';
        btn.setAttribute('data-s6-gift-add', offer.key);
        btn.appendChild(document.createTextNode('Add back'));

        row.appendChild(text);
        row.appendChild(btn);
        list.appendChild(row);
      });
    });
  }

  /*
     Everything this file draws on top of the drawer, in one place.
     The drawer re-renders by replacing its own HTML, which wipes all of it.
     Worse, ONE cart change can cause more than one re-render: assets/custom.js
     runs its own milestone pass straight after ours. So we re-apply on a short
     ladder of delays as well as immediately - otherwise the last re-render wins
     and the "Add back" row silently never appears.
  */
  function reapplyDecorations() {
    if (!lastCart) return;
    injectStyles();
    decorateLines(lastCart);
    markShakerMilestoneUnlocked(milestoneSuppressionOn);
    renderGiftBackSlot(lastCart, lastEarned, lastSuppressed);
  }

  var reapplyTimers = [];
  function reapplySoon() {
    reapplyTimers.forEach(function (t) { clearTimeout(t); });
    reapplyTimers = [120, 400, 900, 1800].map(function (ms) {
      return setTimeout(reapplyDecorations, ms);
    });
    if (window.requestAnimationFrame) window.requestAnimationFrame(reapplyDecorations);
    else reapplyDecorations();
  }

  /* ------------------------------------------------ REMOVE / ADD-BACK CLICKS */

  function refreshDrawer() {
    if (typeof window.themeRefreshCart === 'function') return window.themeRefreshCart(false);
    return fetchCart().then(function (cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true, detail: { cart: cart, openMiniCart: false }
      }));
    });
  }

  // Which gift is this row? Our own freebies carry data-s6-freebie; milestone
  // gifts carry data-s6-milestone-gift (the variant id).
  function giftKeyForRow(row) {
    var key = row.getAttribute('data-s6-freebie');
    if (key) return key;

    var match = null;
    var vid = toId(row.getAttribute('data-s6-milestone-gift'));
    if (vid !== null) {
      milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
      if (match) return match;
    }

    // Last resort: work it out from the variant the row says it holds. Covers a
    // row the JS has not decorated yet (a legacy GoKwik line, say).
    vid = toId(row.getAttribute('data-variant-id'));
    if (vid === null) return null;
    var own = freebieKeyForVariant(vid);
    if (own) return own;
    milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
    return match;
  }

  function handleGiftRemove(btn) {
    var row = btn.closest ? btn.closest('line-item') : null;
    if (!row) return;

    // Mark it dismissed BEFORE the removal request goes out, so the pass that
    // runs when the cart changes cannot race the gift back in.
    setDismissed(giftKeyForRow(row), true);

    // Liquid prints data-line-key on every row; the JS decoration keeps a copy.
    var lineKey = row.getAttribute('data-line-key') || row.getAttribute('data-s6-line-key');
    btn.disabled = true;

    chain = chain.then(function () {
      if (lineKey) return setLineQuantity(lineKey, 0);
      // Fallback: use the drawer's own hidden remove link.
      var link = row.querySelector('[data-remove-link]');
      if (link) link.click();
      return null;
    }).then(function () {
      if (lineKey) return refreshDrawer();
      return null;
    }).then(function () {
      reapplySoon();          // the drawer re-renders; put our row back after it
    }).catch(function (e) { log('gift remove failed', e); });

    return chain;
  }

  function handleGiftAddBack(key, btn) {
    if (!key) return;
    setDismissed(key, false);
    if (btn) btn.disabled = true;

    var variantId = null;
    var props = null;

    if (S6_FREEBIE_CONFIG.freebies[key]) {
      variantId = S6_FREEBIE_CONFIG.freebies[key].variantId;
      props = {};
      props[S6_FREEBIE_CONFIG.propertyName] = key;
    } else {
      var g = milestoneGiftByKey(key);
      if (g) {
        variantId = g.variantId;
        props = { '_type': 'free_gift' };   // same property custom.js writes
      }
    }

    if (!variantId) return;

    // Clearing the dismissal above is all it takes for the milestone engine to
    // own this gift again: the wrapper around addFreeProduct checks
    // isDismissed() fresh on every call, so it stops hiding the product the
    // moment the flag is gone.
    chain = chain.then(function () {
      return addItems([{ id: variantId, quantity: 1, properties: props }]);
    }).then(function () {
      return refreshDrawer();
    }).then(function () {
      reapplySoon();
    }).catch(function (e) { log('gift add back failed', e); });

    return chain;
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || target.nodeType !== 1 || !target.closest) return;

    var removeBtn = target.closest('[data-s6-gift-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftRemove(removeBtn);
      return;
    }

    var addBtn = target.closest('[data-s6-gift-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftAddBack(addBtn.getAttribute('data-s6-gift-add'), addBtn);
    }
  }, true);

  /* ------------------------------------------- ADD THE GIFT WITH THE PRODUCT */
  /*
     When the shopper adds a product that earns a gift, we slip the gift into
     the SAME /cart/add.js request. One request, one cart change, one render.

     The decision has to be made synchronously, while the request is still
     being assembled, so it is based on `cachedCart` (refreshed after every
     cart change) plus the items in the request itself.
  */

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  // Turn "items[0][id]" style keys into a nested object.
  function setDeep(target, path, value) {
    var parts = path.replace(/\]/g, '').split('[');
    var node = target;
    for (var i = 0; i < parts.length - 1; i++) {
      if (typeof node[parts[i]] !== 'object' || node[parts[i]] === null) node[parts[i]] = {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  }

  function formStringToObject(str) {
    var out = {};
    String(str).split('&').forEach(function (pair) {
      if (!pair) return;
      var bits = pair.split('=');
      var k = decodeURIComponent(bits[0].replace(/\+/g, ' '));
      var v = decodeURIComponent((bits[1] || '').replace(/\+/g, ' '));
      setDeep(out, k, v);
    });
    return out;
  }

  // Read the items out of an add-to-cart payload, whatever shape it arrives in:
  // { id, quantity, properties }, { items: [...] }, JSON text, or form-encoded.
  function readAddItems(data) {
    if (!data) return null;

    if (typeof data === 'string') {
      var head = data.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') {
        try { return readAddItems(JSON.parse(data)); } catch (e) { return null; }
      }
      return readAddItems(formStringToObject(data));
    }

    if (typeof data !== 'object') return null;

    if (Object.prototype.toString.call(data.items) === '[object Array]') return data.items.slice();

    // form-encoded items[0][id] parses to an object with "0", "1" ... keys
    if (data.items && typeof data.items === 'object') {
      return Object.keys(data.items).sort().map(function (k) { return data.items[k]; });
    }

    if (data.id) {
      var one = { id: data.id, quantity: data.quantity };
      if (data.properties && typeof data.properties === 'object') one.properties = data.properties;
      return [one];
    }
    return null;
  }

  // Never send an empty or missing field back to Shopify - a stray
  // "properties=" would land on the line as a real (blank) property.
  function cleanItem(it) {
    var q = (it.quantity === undefined || it.quantity === null || it.quantity === '') ? 1 : it.quantity;
    var out = { id: it.id, quantity: q };
    if (it.properties && typeof it.properties === 'object' &&
        Object.keys(it.properties).length) out.properties = it.properties;
    return out;
  }

  function cartHasVariant(cart, variantId) {
    var vid = toId(variantId);
    return (cart.items || []).some(function (item) { return toId(item.variant_id) === vid; });
  }

  // Which gifts should ride along with the items being added?
  function freebiesForAdd(incomingItems) {
    if (!S6_ENGINE_ENABLED) return [];
    var cart = cachedCart;
    if (!cart || !cart.items) return [];      // no cached cart yet: fall back to
                                              // the ordinary pass afterwards
    var now = Date.now();
    var wanted = {};

    S6_FREEBIE_CONFIG.rules.forEach(function (rule) {
      if (!ruleLive(rule, now)) return;
      if (!S6_FREEBIE_CONFIG.freebies[rule.freebie]) return;

      var hit = false;

      // already in the cart...
      (cart.items || []).forEach(function (item) {
        if (hit) return;
        if (isFreebieLine(item) || isLegacyGokwikFreebieLine(item)) return;
        if (ruleHits(rule, toId(item.product_id), toId(item.variant_id))) hit = true;
      });

      // ...or being added right now (a request only tells us the variant)
      incomingItems.forEach(function (it) {
        if (hit) return;
        if (ruleHits(rule, null, toId(it.id))) hit = true;
      });

      if (hit) wanted[rule.freebie] = true;
    });

    var out = [];
    Object.keys(wanted).forEach(function (key) {
      if (isDismissed(key)) return;                                   // shopper said no
      var variantId = S6_FREEBIE_CONFIG.freebies[key].variantId;
      if (cartHasVariant(cart, variantId)) return;                    // already in the cart
      var already = incomingItems.some(function (it) {
        return toId(it.id) === toId(variantId);
      });
      if (already) return;                                            // already in this request
      out.push(freebieItem(key));
    });

    return out;
  }

  // Rebuild the payload in the same shape it came in, with the gifts appended.
  // FormData is deliberately left alone: rewriting a multipart body is fiddly
  // and the ordinary pass afterwards still adds the gift correctly.
  function rewriteAddPayload(original, items, extra) {
    var merged = items.concat(extra).map(cleanItem);

    if (typeof original === 'string') {
      var head = original.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') return JSON.stringify({ items: merged });
      return merged.map(function (it, i) {
        var parts = ['items[' + i + '][id]=' + encodeURIComponent(it.id),
                     'items[' + i + '][quantity]=' + encodeURIComponent(it.quantity || 1)];
        var props = it.properties || {};
        Object.keys(props).forEach(function (p) {
          parts.push('items[' + i + '][properties][' + encodeURIComponent(p) + ']=' +
                     encodeURIComponent(props[p]));
        });
        return parts.join('&');
      }).join('&');
    }

    return { items: merged };
  }

  function maybeAppendFreebies(original) {
    if (s6OwnWrite) return null;
    if (typeof FormData !== 'undefined' && original instanceof FormData) return null;

    var items = readAddItems(original);
    if (!items || !items.length) return null;

    var extra = freebiesForAdd(items);
    if (!extra.length) return null;

    log('adding', extra.length, 'gift(s) in the same request as the product');
    return rewriteAddPayload(original, items, extra);
  }

  // jQuery: prefilters run before jQuery turns `data` into a query string, so
  // the payload is still an editable object at this point.
  if (window.jQuery && window.jQuery.ajaxPrefilter) {
    window.jQuery.ajaxPrefilter(function (options) {
      try {
        if (!isCartAddUrl(options.url)) return;
        var rewritten = maybeAppendFreebies(options.data);
        if (rewritten !== null) options.data = rewritten;
      } catch (e) { log('prefilter error', e); }
    });
  }

  /* -------------------------------------------------------------- TRIGGERS */

  var scheduled = null;
  function schedule(cart) {
    clearTimeout(scheduled);
    // Several cart events often arrive together for one shopper action.
    // Wait for them to settle, then reconcile once.
    scheduled = setTimeout(function () { reconcile(cart); }, 150);
  }

  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    var handler = function (event) {
      // Some code paths dispatch these events with no payload at all, so fall
      // back to fetching the cart ourselves rather than skipping the pass.
      var cart = event && event.detail && event.detail.cart;
      reapplySoon();          // the drawer is about to re-render
      schedule(cart || null);
    };
    document.documentElement.addEventListener(name, handler);
    document.addEventListener(name, handler);
  });

  // Safety net: catch cart writes made by code that never fires an event.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { url = ''; }

      var mine = s6OwnWrite || busy;
      var args = arguments;

      // Slip the gift into the shopper's own add request.
      if (!mine && isCartAddUrl(url) && init && init.body) {
        try {
          var rewritten = maybeAppendFreebies(init.body);
          if (rewritten !== null) {
            var copy = {};
            for (var k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) copy[k] = init[k]; }
            copy.body = rewritten;
            args = [input, copy];
          }
        } catch (e) { log('fetch rewrite error', e); }
      }

      var result = nativeFetch.apply(this, args);

      if (!mine && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        result.then(function () { schedule(null); }).catch(function () {});
      }
      return result;
    };
  }

  if (window.jQuery) {
    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (busy || !settings || !settings.url) return;
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(settings.url)) schedule(null);
    });
  }

  /* ----------------------------------------------------------------- BOOT  */

  // The drawer re-renders by swapping its own innerHTML, which wipes the
  // classes we added. Re-apply them whenever that happens, without kicking
  // off another reconciliation (that would loop).
  var watchTries = 0;

  function watchForRerenders() {
    if (!window.MutationObserver) return;

    var drawer = document.getElementById('mini-cart');

    // The drawer is not always in the page on the first pass. It used to give
    // up here and then nothing ever restored our rows after a re-render, so
    // keep looking for a few seconds instead.
    if (!drawer) {
      if (watchTries++ < 40) setTimeout(watchForRerenders, 250);
      return;
    }
    if (drawer.__s6Watched) return;
    drawer.__s6Watched = true;

    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued || !lastCart) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        reapplyDecorations();
      });
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  function boot() {
    // Master switch (Theme settings > Supply6 Freebies > Enable Supply6
    // freebie engine). Off means no styles, no patching of the milestone
    // engine, no observers and no cart writes — a complete no-op.
    if (!S6_ENGINE_ENABLED) {
      log('disabled in Theme settings > Supply6 Freebies — doing nothing');
      return;
    }

    injectStyles();

    // custom.js is deferred; this file is deferred and comes after it, so the
    // milestone engine should already exist. Retry briefly just in case.
    if (!patchMilestoneEngine()) {
      var tries = 0;
      var t = setInterval(function () {
        if (patchMilestoneEngine() || ++tries > 20) clearInterval(t);
      }, 100);
    }

    watchForRerenders();

    // Prime the cached cart straight away so the very first add-to-cart of the
    // session can already carry its gift.
    reconcile(null);
  }

  // Small handle for debugging from the browser console, e.g.
  //   S6Freebies.config          — see the live rules
  //   S6Freebies.reconcile()     — force a pass
  //   S6Freebies.plan(cart)      — see what a given cart would trigger
  window.S6Freebies = {
    enabled: S6_ENGINE_ENABLED,
    config: S6_FREEBIE_CONFIG,
    reconcile: function () { return reconcile(null); },
    plan: planFor,
    activeRules: activeRuleIds,
    desired: desiredFreebies,
    // S6Freebies.dismissed()      — gifts the shopper removed this session
    // S6Freebies.undismissAll()   — forget all of them
    dismissed: readDismissed,
    undismissAll: function () { writeDismissed({}); return reconcile(null); },
    // S6Freebies.offers()     — what the "Add back" row should be showing now
    // S6Freebies.milestones() — the cart-value gifts and their rupee thresholds
    offers: function () {
      return lastCart ? giftBackOffers(lastCart, lastEarned, lastSuppressed) : [];
    },
    milestones: milestoneGifts,
    redraw: reapplyDecorations,
    // S6Freebies.coAdd([{id: <variant>, quantity: 1}]) — what would ride along
    coAdd: freebiesForAdd,
    cart: function () { return cachedCart; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();